'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';

const BUSINESS_UNIT_ID = '68dbf7143b6dc12a3dc2db59';

type Locale = 'de-DE' | 'en-US';
type Variant = 'micro-star' | 'mini' | 'micro-combo' | 'micro-trustscore';

const VARIANTS: Record<
  Variant,
  { templateId: string; height: string; theme?: 'light' | 'dark' }
> = {
  'micro-star': {
    templateId: '5419b732fbfb950b10de65e5',
    height: '24px',
  },
  mini: {
    templateId: '53aa8807dec7e10d38f59f32',
    height: '150px',
    theme: 'dark',
  },
  'micro-combo': {
    templateId: '5419b6ffb0d04a076446a9af',
    height: '20px',
  },
  'micro-trustscore': {
    templateId: '5419b637fa0340045cd0c936',
    height: '20px',
    theme: 'dark',
  },
};

const TOKENS: Record<Variant, Partial<Record<Locale, string>>> = {
  'micro-star': {
    'de-DE': '6e76b60a-d969-4e7f-af1e-33a45f0d1f48',
    'en-US': '628b1021-5223-40d6-b38e-91b78f266477',
  },
  mini: {
    'de-DE': '170c6f06-8999-4281-84f9-a453514a3d5d',
    'en-US': '2ac31787-5043-4fec-a694-8a41aa3d0d8e',
  },
  'micro-combo': {
    'de-DE': 'bd586871-b272-4328-9fd2-e0eda321371d',
    'en-US': '9df43821-c52e-4b25-9db5-6c737c5f922a',
  },
  'micro-trustscore': {
    'de-DE': '0dcf8805-a908-46d1-99a5-2a1b410e2488',
    'en-US': 'c46d46ef-50c5-4b11-a670-af861c1063a3',
  },
};

export const REVIEW_URLS: Record<Locale, string> = {
  'de-DE': 'https://de.trustpilot.com/review/iqcapital.io',
  'en-US': 'https://www.trustpilot.com/review/iqcapital.io',
};

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (el: HTMLElement, init?: boolean) => void;
    };
  }
}

interface TrustpilotWidgetProps {
  variant: Variant;
  locale?: Locale;
  /** Override token. Required for locales without a default token configured. */
  token?: string;
  /** Override widget theme. Useful when placing on a dark/light background that differs from the variant default. */
  theme?: 'light' | 'dark';
  className?: string;
}

export function TrustpilotWidget({
  variant,
  locale = 'de-DE',
  token,
  theme,
  className,
}: TrustpilotWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const config = VARIANTS[variant];
  const resolvedToken = token ?? TOKENS[variant][locale];
  const resolvedTheme = theme ?? config.theme;

  useEffect(() => {
    if (ref.current && window.Trustpilot) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, [variant, locale, resolvedToken]);

  if (!resolvedToken) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[TrustpilotWidget] Missing token for variant="${variant}" locale="${locale}". Pass a "token" prop or add it to TOKENS.`,
      );
    }
    return null;
  }

  return (
    <div
      ref={ref}
      className={`trustpilot-widget ${className ?? ''}`.trim()}
      data-locale={locale}
      data-template-id={config.templateId}
      data-businessunit-id={BUSINESS_UNIT_ID}
      data-style-height={config.height}
      data-style-width="100%"
      data-theme={resolvedTheme}
      data-token={resolvedToken}
    >
      <Link href={REVIEW_URLS[locale]} target="_blank" rel="noopener">
        Trustpilot
      </Link>
    </div>
  );
}

export function TrustpilotMicroStar(
  props: Omit<TrustpilotWidgetProps, 'variant'>,
) {
  return <TrustpilotWidget variant="micro-star" {...props} />;
}

export function TrustpilotMini(props: Omit<TrustpilotWidgetProps, 'variant'>) {
  return <TrustpilotWidget variant="mini" {...props} />;
}

export function TrustpilotMicroCombo(
  props: Omit<TrustpilotWidgetProps, 'variant'>,
) {
  return <TrustpilotWidget variant="micro-combo" {...props} />;
}

export function TrustpilotMicroTrustScore(
  props: Omit<TrustpilotWidgetProps, 'variant'>,
) {
  return <TrustpilotWidget variant="micro-trustscore" {...props} />;
}
